/**
 * Pantalla de **Inventario** (`/inventario`). Reemplaza el `PlaceholderPage` de
 * `ROUTES.inventario`. Equivale a `inventario.html` del SSR, en su parte de
 * **rollos** (galvanizada por kg / acero por metros): listar, añadir, consumir y
 * borrar lotes de la sede activa, más el historial de consumo.
 *
 * Multi-sede: todas las operaciones van contra la **sede activa** porque `lib/api`
 * inyecta `X-Sede-Id` (la sede que el usuario opera); al cambiar de sede, los
 * rollos se recargan (la query se segmenta por sede). El acceso lo gobierna
 * `RequireInventario` (admin siempre; el resto, `view_inventory` + módulo activo en
 * la sede, igual que el menú). La gestión (añadir/consumir/borrar) se ofrece solo
 * con `manage_inventory`.
 *
 * Orquesta hooks + componentes presentacionales; sin `fetch`. Estados de
 * carga/error contemplados.
 */
import { useState } from "react";

import { Button, Card, CardContent } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { MANAGE_PERMISSION } from "../constants";
import {
  formatKg,
  formatMetros,
  inventarioErrorMessage,
  totalKgGalv,
  totalLotesAcero,
  totalLotesGalv,
  totalMetrosAcero,
} from "../format";
import { useRollos } from "../hooks/useInventarioData";
import { useDeleteRollo } from "../hooks/useInventarioMutations";
import type { RolloTipo } from "../types";
import { AddRolloDialog } from "./AddRolloDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import { ConsumeRolloDialog, type ConsumeTarget } from "./ConsumeRolloDialog";
import { HistorialPanel } from "./HistorialPanel";
import { RequireInventario } from "./RequireInventario";
import { RollosList } from "./RollosList";

/** Pestaña activa de la pantalla. */
type Tab = "rollos" | "historial";

/** Objetivo de borrado (id + etiqueta para el diálogo de confirmación). */
type DeleteTarget = { id: number; label: string } | null;

export function InventarioScreen() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(MANAGE_PERMISSION);

  const rollosQuery = useRollos();
  const deleteRollo = useDeleteRollo();

  const [tab, setTab] = useState<Tab>("rollos");
  const [addOpen, setAddOpen] = useState(false);
  const [addTipo, setAddTipo] = useState<RolloTipo>("galv");
  const [consumeTarget, setConsumeTarget] = useState<ConsumeTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  function openAdd(tipo: RolloTipo) {
    setAddTipo(tipo);
    setAddOpen(true);
  }

  function openDelete(rolloId: number, label: string) {
    deleteRollo.reset();
    setDeleteTarget({ id: rolloId, label });
  }

  function handleConfirmDelete() {
    if (deleteTarget === null) {
      return;
    }
    deleteRollo.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <RequireInventario mode="inventario" title="Inventario">
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">Inventario</h1>
          {canManage ? (
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => openAdd("galv")}>
                Agregar galvanizada
              </Button>
              <Button type="button" variant="primary" onClick={() => openAdd("acero")}>
                Agregar acero
              </Button>
            </div>
          ) : null}
        </div>

        {/* KPIs de la sede activa */}
        {rollosQuery.isSuccess ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard label="Lotes galvanizada" value={String(totalLotesGalv(rollosQuery.data.galv))} />
            <KpiCard label="Peso galvanizada" value={formatKg(totalKgGalv(rollosQuery.data.galv))} />
            <KpiCard label="Lotes acero/alu" value={String(totalLotesAcero(rollosQuery.data.acero))} />
            <KpiCard label="Metros acero/alu" value={formatMetros(totalMetrosAcero(rollosQuery.data.acero))} />
          </div>
        ) : null}

        {/* Pestañas Rollos / Historial */}
        <div role="tablist" aria-label="Vistas de inventario" className="flex gap-1 border-b border-slate-200">
          <TabButton active={tab === "rollos"} onClick={() => setTab("rollos")}>
            Rollos
          </TabButton>
          <TabButton active={tab === "historial"} onClick={() => setTab("historial")}>
            Historial
          </TabButton>
        </div>

        <Card>
          <CardContent>
            {rollosQuery.isPending ? (
              <p className="py-8 text-center text-sm text-slate-500" role="status">
                Cargando inventario…
              </p>
            ) : rollosQuery.isError ? (
              <p className="py-8 text-center text-sm text-red-600" role="alert">
                {inventarioErrorMessage(rollosQuery.error)}
              </p>
            ) : tab === "rollos" ? (
              <RollosList
                galv={rollosQuery.data.galv}
                acero={rollosQuery.data.acero}
                canManage={canManage}
                onConsume={setConsumeTarget}
                onDelete={openDelete}
              />
            ) : (
              <HistorialPanel hist={rollosQuery.data.hist} />
            )}
          </CardContent>
        </Card>
      </div>

      {canManage ? (
        <>
          <AddRolloDialog open={addOpen} initialTipo={addTipo} onClose={() => setAddOpen(false)} />
          <ConsumeRolloDialog target={consumeTarget} onClose={() => setConsumeTarget(null)} />
          <ConfirmDialog
            open={deleteTarget !== null}
            title="Eliminar lote"
            description={
              deleteTarget
                ? `¿Eliminar el lote "${deleteTarget.label}"? Esta acción no se puede deshacer.`
                : ""
            }
            errorMessage={deleteRollo.isError ? inventarioErrorMessage(deleteRollo.error) : null}
            isPending={deleteRollo.isPending}
            onConfirm={handleConfirmDelete}
            onClose={() => setDeleteTarget(null)}
          />
        </>
      ) : null}
    </RequireInventario>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
}

function KpiCard({ label, value }: KpiCardProps) {
  return (
    <Card>
      <CardContent>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      </CardContent>
    </Card>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-brand text-brand"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}
